import { useQuery } from '@apollo/client';
import { PARKING_SESSION_DETAIL_QUERY } from './parking-sessions.gql';
import { ParkingSessionDetailsTabs } from './parking-session-details-tabs';
import type { ParkingSessionDetail } from './types';
import { CancelReason, ParkingSessionStatus } from '@/graphql/generated';
import { calculateDuration, formatCustomerName, getParkingSessionDisplayStatus } from './utils';
import { useRef, useState } from 'react';
import { Ban, Loader2, LogOut, ParkingSquare } from 'lucide-react';
import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { ParkingSessionStatusBadge } from './status-badge';

interface ParkingSessionDetailsQueryProps {
	parkingSessionId: string;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

interface ParkingSessionNode {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	exitDateTime?: string | null;
	paymentDateTime?: string | null;
	paymentAmount?: number | null;
	vehicleId?: string | null;
	parkingSessionTypeId?: number | null;
	paymentMethodId?: number | null;
	customerId?: string | null;
	parkingPositionId?: string | null;
	contractId?: string | null;
	status: ParkingSessionStatus;
	cancelReason?: CancelReason | null;
	cashierShiftId: string;
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
		make?: string | null;
		model?: string | null;
		color?: string | null;
		rfidTag?: string | null;
		vehicleType?: {
			vehicleTypeId: number;
			name?: string | null;
		} | null;
	} | null;
	parkingSessionType?: {
		parkingSessionTypeId: number;
		name?: string | null;
	} | null;
	paymentMethod?: {
		paymentMethodId: number;
		name?: string | null;
	} | null;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
		email?: string | null;
	} | null;
	parkingPosition?: {
		parkingPositionId: string;
		code?: string | null;
	} | null;
	contract?: {
		contractId: string;
		amount?: number | null;
		isActive?: boolean | null;
		customer?: {
			customerId: string;
			firmName?: string | null;
			firstName?: string | null;
			lastName?: string | null;
			isCompany?: boolean | null;
		} | null;
	} | null;
	cashierShift?: {
		cashierShiftId: string;
		shiftStartDateTime?: string | null;
		shiftEndDateTime?: string | null;
		user?: {
			userId: string;
			firstName?: string | null;
			lastName?: string | null;
		} | null;
	} | null;
	services?: Array<{
		parkingSessionServiceId: string;
		priceCharged?: number | null;
		service?: {
			serviceId: string;
			description?: string | null;
			code?: string | null;
			price?: number | null;
		} | null;
	}> | null;
	voucherParkingSessions?: Array<{
		voucherParkingSessionId: string;
		appliedAmount?: number | null;
		voucher?: {
			voucherId: string;
			code?: string | null;
			name?: string | null;
			voucherDiscountType?: {
				name?: string | null;
			} | null;
		} | null;
	}> | null;
	prepaidCardTransactions?: Array<{
		prepaidCardTransactionId: string;
		amount?: number | null;
		transactionType?: {
			name?: string | null;
		} | null;
		prepaidCard?: {
			prepaidCardId: string;
			prepaidCode?: string | null;
		} | null;
	}> | null;
	aadeInvoices?: Array<{
		aadeInvoiceId: string;
		invoiceUid?: string | null;
		invoiceMark?: string | null;
		series?: string | null;
	}> | null;
	reservations?: Array<{
		reservationId: string;
		reservationStartTime: string;
		reservationEndTime: string;
		reservationStatus?: {
			name?: string | null;
		} | null;
	}> | null;
}

interface ParkingSessionDetailQueryResponse {
	parkingSession?: ParkingSessionNode | null;
}

const mapNodeToDetail = (node: ParkingSessionNode): ParkingSessionDetail => {
	const durationMinutes = calculateDuration(node.entryDateTime, node.exitDateTime);
	const cashierName =
		node.cashierShift?.user?.firstName && node.cashierShift?.user?.lastName
			? `${node.cashierShift.user.firstName} ${node.cashierShift.user.lastName}`
			: undefined;
	const displayStatus = getParkingSessionDisplayStatus(node.status);

	return {
		parkingSessionId: node.parkingSessionId,
		ticketId: node.ticketId,
		entryDateTime: node.entryDateTime,
		exitDateTime: node.exitDateTime,
		paymentDateTime: node.paymentDateTime,
		paymentAmount: node.paymentAmount,
		vehicleId: node.vehicleId,
		vehiclePlateNumber: node.vehicle?.plateNumber ?? 'Unknown Vehicle',
		vehicleMake: node.vehicle?.make ?? undefined,
		vehicleModel: node.vehicle?.model ?? undefined,
		vehicleColor: node.vehicle?.color ?? undefined,
		vehicleTypeName: node.vehicle?.vehicleType?.name ?? undefined,
		vehicleRfidTag: (node.contract as any)?.contractVehicles?.find((cv: any) => cv.vehicleId === node.vehicleId)?.rfidTag ?? undefined,
		parkingSessionType: node.parkingSessionType?.name ?? undefined,
		paymentMethod: node.paymentMethod?.name ?? undefined,
		customerId: node.customerId,
		customerName: formatCustomerName(
			node.customer?.isCompany,
			node.customer?.firmName,
			node.customer?.firstName,
			node.customer?.lastName
		),
		customerEmail: node.customer?.email ?? undefined,
		parkingPositionCode: node.parkingPosition?.code ?? undefined,
		contractId: node.contractId,
		contractCustomerName: formatCustomerName(
			node.contract?.customer?.isCompany,
			node.contract?.customer?.firmName,
			node.contract?.customer?.firstName,
			node.contract?.customer?.lastName
		),
		contractAmount: node.contract?.amount ?? undefined,
		contractIsActive: node.contract?.isActive ?? undefined,
		cashierShiftId: node.cashierShiftId,
		cashierName,
		cashierShiftStart: node.cashierShift?.shiftStartDateTime ?? undefined,
		cashierShiftEnd: node.cashierShift?.shiftEndDateTime ?? undefined,
		durationMinutes: durationMinutes ?? undefined,
		status: node.status,
		cancelReason: node.cancelReason,
		displayStatus,
		vehicleJourneyLogs: [], // Journey logs are loaded separately via ParkingSessionJourneyLogs component
		services:
			node.services?.map((service) => ({
				parkingSessionServiceId: service.parkingSessionServiceId,
				serviceName: service.service?.description ?? 'Unknown',
				serviceCode: service.service?.code ?? undefined,
				priceCharged: service.priceCharged,
				standardPrice: service.service?.price ?? undefined
			})) ?? [],
		voucherUsages:
			node.voucherParkingSessions?.map((vps) => ({
				voucherParkingSessionId: vps.voucherParkingSessionId,
				voucherCode: vps.voucher?.code ?? 'Unknown',
				voucherName: vps.voucher?.name ?? undefined,
				discountTypeName: vps.voucher?.voucherDiscountType?.name ?? undefined,
				amountApplied: vps.appliedAmount
			})) ?? [],
		prepaidCardTransactions:
			node.prepaidCardTransactions?.map((transaction) => ({
				prepaidCardTransactionId: transaction.prepaidCardTransactionId,
				cardNumber: transaction.prepaidCard?.prepaidCode ?? 'Unknown',
				transactionTypeName: transaction.transactionType?.name ?? 'Unknown',
				amount: transaction.amount
			})) ?? [],
		reservations:
			node.reservations?.map((reservation) => ({
				reservationId: reservation.reservationId,
				reservationDateTime: reservation.reservationStartTime,
				arrivalDateTime: reservation.reservationEndTime,
				statusName: reservation.reservationStatus?.name ?? undefined
			})) ?? [],
		aadeInvoices: node.aadeInvoices ?? []
	};
};

export const ParkingSessionDetailsQuery = ({
	parkingSessionId,
	showHeader = false,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: ParkingSessionDetailsQueryProps) => {
	const [activeTab, setActiveTab] = useState('details');
	// Keep track of the last valid session data to prevent jitter during loading
	const lastSessionRef = useRef<ParkingSessionDetail | null>(null);

	const { data, loading, error } = useQuery<ParkingSessionDetailQueryResponse>(
		PARKING_SESSION_DETAIL_QUERY,
		{
			variables: { parkingSessionId },
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		}
	);

	// Update the ref when we get new data
	if (data?.parkingSession) {
		lastSessionRef.current = mapNodeToDetail(data.parkingSession);
	}

	// Use the cached session while loading to prevent jitter
	const session = data?.parkingSession
		? mapNodeToDetail(data.parkingSession)
		: lastSessionRef.current;

	// Show loading state only on initial load (no cached data)
	if (loading && !session) {
		return (
			<div className="flex items-center justify-center p-8">
				<Loader2 className="w-5 h-5 mr-2 animate-spin text-muted-foreground" />
				<p className="text-sm text-muted-foreground">Loading details...</p>
			</div>
		);
	}

	if (error && !session) {
		return (
			<div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
				<p className="text-sm text-destructive">
					Failed to load parking session details
				</p>
			</div>
		);
	}

	if (!session) {
		return (
			<div className="rounded-md border border-muted p-4">
				<p className="text-sm text-muted-foreground">
					Parking session not found
				</p>
			</div>
		);
	}

	const getHeaderIcon = (status: ParkingSessionStatus) => {
		if (status === ParkingSessionStatus.Inside) {
			return (
				<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-blue-400 dark:border-blue-500 flex-shrink-0">
					<ParkingSquare className="w-7 h-7 text-blue-600 dark:text-blue-400" />
				</div>
			);
		}

		if (status === ParkingSessionStatus.Canceled) {
			return (
				<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-red-400 dark:border-red-500 flex-shrink-0">
					<Ban className="w-7 h-7 text-red-600 dark:text-red-400" />
				</div>
			);
		}

		return (
			<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
				<LogOut className="w-7 h-7 text-gray-500 dark:text-gray-400" />
			</div>
		);
	};

	const title = session.ticketId
		? `${session.vehiclePlateNumber} - ${session.ticketId}`
		: session.vehiclePlateNumber;

	return (
		<div className="flex flex-col h-full">
			{showHeader && (
				<EntityDetailsHeader
					icon={getHeaderIcon(session.status)}
					name={title}
					statusBadge={
						<ParkingSessionStatusBadge status={session.displayStatus} />
					}
					entityTypeName={session.parkingSessionType}
					entityId={session.parkingSessionId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={Boolean(onClose)}
					className="pb-4 mb-4"
				/>
			)}
			<ParkingSessionDetailsTabs
				session={session}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			/>
		</div>
	);
};
